import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { LoadError } from "../components/common/ui";
import { useAuth } from "../context/AuthContext";
import { errorText, get, post } from "../lib/api";
import { useHead, usePub } from "./PublicSite";
import { Markdown } from "./Markdown";
import { Cover, FreeCta, Loading, NotFound, usePublicApi } from "./contentShared";

const useProgress = (course) => {
  const { me } = useAuth();
  const [p, setP] = useState(null);
  const load = useCallback(() => { if (me && course) get(`/academy/progress/${course}`).then(setP).catch(() => setP(null)); }, [me, course]);
  useEffect(load, [load]);
  return [me ? p : null, setP];
};

const ProgressBar = ({ done, total }) => {
  const { t, fmtNum } = useI18n();
  const pct = total ? Math.round((100 * done) / total) : 0;
  return (
    <div className="pub-progress" data-testid="course-progress">
      <div className="flex justify-between text-sm"><span>{t("academy.progress")}</span>
        <span data-testid="course-progress-text">{t("academy.progressOf", { done: fmtNum(done), total: fmtNum(total), pct: fmtNum(pct) })}</span></div>
      <div className="pub-progress-track"><div className="pub-progress-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const LoginHint = () => {
  const { t } = useI18n();
  const { me } = useAuth();
  const location = useLocation();
  if (me) return null;
  return <p className="mt-4 text-sm" data-testid="academy-login-hint"><Link to={`/login?next=${encodeURIComponent(location.pathname)}`} className="pub-link underline">{t("academy.loginToSave")}</Link></p>;
};

export const AcademyIndex = () => {
  const { lang } = usePub();
  const { t } = useI18n();
  const { data } = usePublicApi("/public/academy", lang);
  useHead(t("academy.metaTitle"), t("academy.metaDesc"));
  if (!data) return <Loading />;
  return (
    <div className="pub-section" data-testid="academy-page">
      <div className="pub-wrap">
        <p className="pub-kicker is-light">{t("academy.kicker")}</p>
        <h1 className="pub-h1">{t("academy.title")}</h1>
        <p className="pub-lead">{t("academy.lead")}</p>
        {data.items.length === 0 && <p className="pub-muted mt-8" data-testid="academy-empty">{t("blog.empty")}</p>}
        <div className="mt-8 grid gap-6">{data.items.map((c) => (
          <Link key={c.slug} to={`/${lang}/academy/${c.slug}`} className="pub-course-card" data-testid={`course-card-${c.slug}`}>
            {c.cover_url && <Cover url={c.cover_url} sizes="(min-width: 768px) 40vw, 100vw" />}
            <div className="grid content-center gap-3 p-6">
              <span className="pub-chip w-fit">{t("academy.lessons", { count: c.lessons })}</span>
              <h2 className="pub-post-title">{c.title}</h2>
              <p className="pub-muted m-0">{c.excerpt}</p>
              <span className="pub-link inline-flex items-center gap-1">{t("academy.start")} <ArrowRight className="rtl-flip h-4 w-4" /></span>
            </div>
          </Link>))}
        </div>
        <FreeCta id="academy-index" />
      </div>
    </div>
  );
};

export const CoursePage = () => {
  const { course } = useParams();
  const { lang } = usePub();
  const { t } = useI18n();
  const { data, error } = usePublicApi(`/public/academy/${course}`, lang);
  const [prog] = useProgress(course);
  useHead(data ? `${data.title} | Auditmail` : t("academy.metaTitle"), data ? data.meta_desc : t("academy.metaDesc"),
          { langs: data ? data.langs : undefined, image: data && data.cover_url, type: "article" });
  if (error) return <LoadError error={error} onRetry={() => window.location.reload()} notFound={<NotFound back="/academy" label={t("academy.back")} />} />;
  if (!data) return <Loading />;
  const done = new Set(prog ? prog.completed : []);
  return (
    <div className="pub-section" data-testid="course-page">
      <div className="pub-wrap pub-article">
        <Link to={`/${lang}/academy`} className="pub-link inline-flex items-center gap-1" data-testid="course-back-link"><ArrowLeft className="rtl-flip h-4 w-4" />{t("academy.back")}</Link>
        <h1 className="pub-h1 pub-article-h1 mt-6" data-testid="course-title">{data.title}</h1>
        <p className="pub-lead">{data.excerpt}</p>
        {prog && <ProgressBar done={data.lessons.filter((l) => done.has(l.slug)).length} total={data.lessons.length} />}
        <LoginHint />
        <ol className="pub-lesson-list" data-testid="course-lessons">{data.lessons.map((l, i) => (
          <li key={l.slug}>
            <Link to={`/${lang}/academy/${course}/${l.slug}`} data-testid={`lesson-link-${l.slug}`}>
              {done.has(l.slug) ? <CheckCircle2 className="h-5 w-5 text-[#15803d]" data-testid={`lesson-done-${l.slug}`} /> : <Circle className="h-5 w-5 text-zinc-300" />}
              <span className="pub-lesson-n">{i + 1}</span><span className="flex-1">{l.title}</span>
              <span className="pub-muted text-sm">{t("blog.readMin", { count: l.read_min })}</span>
            </Link>
          </li>))}
        </ol>
        {data.lessons[0] && <Link to={`/${lang}/academy/${course}/${data.lessons[0].slug}`} className="pub-btn pub-btn-dark mt-6" data-testid="course-start-button">{t("academy.start")}</Link>}
        <Markdown source={data.body} testId="course-body" />
        <FreeCta id="course" />
      </div>
    </div>
  );
};

export const LessonPage = () => {
  const { course, lesson } = useParams();
  const { lang } = usePub();
  const { t } = useI18n();
  const { me } = useAuth();
  const { data, error } = usePublicApi(`/public/academy/${course}/${lesson}`, lang);
  const [prog, setProg] = useProgress(course);
  const [busy, setBusy] = useState(false);
  useHead(data ? `${data.title} | Auditmail` : t("academy.metaTitle"), data ? data.meta_desc : t("academy.metaDesc"),
          { langs: data ? data.langs : undefined, image: data && data.cover_url, type: "article" });
  if (error) return <LoadError error={error} onRetry={() => window.location.reload()} notFound={<NotFound back={`/academy/${course}`} label={t("academy.backCourse")} />} />;
  if (!data) return <Loading />;
  const done = new Set(prog ? prog.completed : []);
  const isDone = done.has(lesson);
  const toggle = async () => {
    setBusy(true);
    try { setProg(await post(`/academy/progress/${course}/${lesson}`, { completed: !isDone })); } catch (e) { toast.error(errorText(t, e)); }
    setBusy(false);
  };
  return (
    <article className="pub-section" data-testid="lesson-page">
      <div className="pub-wrap pub-article">
        <Link to={`/${lang}/academy/${course}`} className="pub-link inline-flex items-center gap-1" data-testid="lesson-course-link"><ArrowLeft className="rtl-flip h-4 w-4" />{data.course.title}</Link>
        <div className="mt-6"><span className="pub-kicker is-light" data-testid="lesson-index">{t("academy.lessonOf", { n: data.index + 1, total: data.lessons.length })}</span></div>
        <h1 className="pub-h1 pub-article-h1" data-testid="lesson-title">{data.title}</h1>
        {prog && <ProgressBar done={data.lessons.filter((l) => done.has(l.slug)).length} total={data.lessons.length} />}
        <Markdown source={data.body} testId="lesson-body" />
        <div className="pub-lesson-actions">
          {me ? (
            <button className={`pub-btn ${isDone ? "pub-btn-ghost" : "pub-btn-dark"}`} disabled={busy || !prog} onClick={toggle} data-testid="lesson-complete-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}<span>{t(isDone ? "academy.completed" : "academy.markComplete")}</span>
            </button>) : <LoginHint />}
          <div className="flex gap-2">
            {data.prev && <Link to={`/${lang}/academy/${course}/${data.prev.slug}`} className="pub-btn pub-btn-ghost" data-testid="lesson-prev"><ArrowLeft className="rtl-flip h-4 w-4" />{t("academy.prev")}</Link>}
            {data.next && <Link to={`/${lang}/academy/${course}/${data.next.slug}`} className="pub-btn pub-btn-ghost" data-testid="lesson-next">{t("academy.next")}<ArrowRight className="rtl-flip h-4 w-4" /></Link>}
          </div>
        </div>
        <FreeCta id="lesson" />
      </div>
    </article>
  );
};
